import { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { MapareRow, MedocCandidate } from '@lyra/shared';
import { Button } from '@/components/ui/button';
import { formatNumberRo } from '@/lib/format';
import type { WizardStepId } from '../lib/wizard';
import { CorrectionPanel } from '../CorrectionPanel';
import { useBulkResolve, useResolveEntry, useSplitEntry } from '../api/reviewHooks';
import { defaultSelectedIds, needsBulkConfirm, primaryActionLabel } from './WizardListStep';

export interface WizardReviewStepProps {
  /** Which `single`/`masterdetail`-mode step this renders (`ambiguu`/`revizuire`/
   *  `low_confidence` — `WIZARD_STEPS`, T1). Only `ambiguu` gets the Task 5
   *  candidate-card + split view below; the other two stay on `CorrectionPanel`. */
  stepId: WizardStepId;
  /** Already-bucketed rows for the current step (`ambiguu`/`revizuire`/`low_confidence` —
   *  `WIZARD_STEPS`' `single`/`masterdetail` modes, T1). */
  rows: MapareRow[];
  runId: number;
  /** T3c-3: true on a finalized run — passed straight through to `CorrectionPanel`
   *  (which already disables every mutating control while showing reasoning/
   *  candidates) and to `canSplitRow` for the `ambiguu` branch below. */
  finalized: boolean;
}

/**
 * A row's MEDOC candidates in the order the operator sees them: primary
 * first, then alternates. This ordering IS the index space `candidateIndexes`
 * uses on the wire (`POST /runs/:id/entries/:entryId/split`), and the server
 * rebuilds the same list from `[primary_candidate, ...alternate_candidates]`
 * with the same `.filter(Boolean)` — a row with no primary collapses the list
 * rather than leaving a hole, on both sides.
 */
export function candidatesOf(row: MapareRow): MedocCandidate[] {
  return [row.primaryCandidate, ...(row.alternateCandidates ?? [])].filter(Boolean) as MedocCandidate[];
}

/** What ticking a set of candidates on one `ambiguu` row actually means. */
export type AmbiguousAction =
  /** ≥2 ticked — generate one child row per ticked candidate. */
  | { kind: 'split'; indexes: number[] }
  /** Exactly 1 ticked — not a split at all: that candidate IS the answer. */
  | { kind: 'resolve'; candidate: MedocCandidate; index: number; isPrimary: boolean }
  /** Nothing actionable (no ticks, or the run is finalized). */
  | { kind: 'none' };

/**
 * Pure decision behind the `ambiguu` step's primary control (2026-08-21 UI
 * report: „la ambigue avem nevoie si de o bifa, in cazul in care o mapare
 * este incorecta"). Splitting used to be all-or-nothing, so a row that
 * matched three codes — one of them plainly wrong — could only be expanded
 * into all three or abandoned as unresolved.
 *
 * Untick the wrong one and the remaining two split. Untick down to ONE and
 * it stops being a split: a single candidate is „this one is the answer",
 * which is a plain resolve — and it's what the server would reject as
 * `too_few` anyway, so the client must not send it as a split.
 */
export function ambiguousAction(
  row: MapareRow,
  checked: Set<number>,
  finalized: boolean
): AmbiguousAction {
  if (finalized) return { kind: 'none' };
  const candidates = candidatesOf(row);
  const indexes = [...checked].filter((i) => i >= 0 && i < candidates.length).sort((a, b) => a - b);
  if (indexes.length === 0) return { kind: 'none' };
  if (indexes.length === 1) {
    const index = indexes[0];
    return {
      kind: 'resolve',
      candidate: candidates[index],
      index,
      isPrimary: row.primaryCandidate != null && index === 0,
    };
  }
  return { kind: 'split', indexes };
}

/** The patch a single-candidate `resolve` writes — mirrors `CorrectionPanel`'s own
 *  „Confirmă" / „Alege această variantă" payloads so a decision taken here is
 *  indistinguishable from the same decision taken there. */
function resolvePatch(action: Extract<AmbiguousAction, { kind: 'resolve' }>): Partial<MapareRow> {
  const { candidate, isPrimary } = action;
  return {
    reviewResolution: isPrimary ? 'confirmed_primary' : 'selected_alternate',
    codMedoc: candidate.codMedoc,
    denumireMedoc: candidate.denumireMedoc,
    codBdMedoc: candidate.codBdMedoc,
    status: isPrimary ? 'classified' : 'edited',
  };
}

/**
 * Judgement-style wizard steps (`single`/`masterdetail` in `WIZARD_STEPS`).
 * v1 split these across `WizardSingleStep.tsx` (one row at a time, ambiguous
 * only) and `ReviewQueueView.tsx` (master-detail list, revizuire/low_confidence)
 * — both re-implemented their own primary/alternate/unresolved/manual-map
 * controls. v2's `CorrectionPanel` (Plan 3c-2) already IS that control set,
 * generalized to every workflow status (primary suggestion, alternates,
 * voting comparison, nomenclator search, manual edit, „Fără corespondent"),
 * so `revizuire`/`low_confidence` don't reimplement anything — it's an
 * editorial list of the step's rows, each with its `CorrectionPanel`
 * rendered inline (no modal, no master-detail split — Direction B).
 * `low_confidence`'s "manual nomenclator map" requirement is already
 * satisfied: the panel's nomenclator search section is unconditional (not
 * gated per bucket).
 *
 * `ambiguu` is the one exception: a row matching several MEDOC codes at once
 * isn't "pick the right single answer", it's "these may all be legitimately
 * separate line items" (e.g. a bundled lab panel), so it renders candidate
 * cards plus terminal actions — split the row into one child per candidate
 * (`useSplitEntry`) or mark it unresolved (`useResolveEntry`).
 *
 * 2026-08-21 UI report — the `ambiguu` step gained TWO independent selections:
 *   - per CANDIDATE (`candidateSel`), so a plainly-wrong mapping is unticked
 *     and never becomes its own row. `ambiguousAction` above turns the ticks
 *     into a split, a plain resolve (one tick) or nothing.
 *   - per ROW (`selected`), driving a bulk „Generează separat pentru N
 *     rânduri" bar — the step previously had no way to act on more than one
 *     row at a time („nu exista buton de genereaza tot la ambigue").
 * Both selections live HERE, not inside the per-row card: the list is
 * virtualized, so an off-screen row's card is unmounted and any state it
 * owned would silently reset on scroll.
 */
export function WizardReviewStep({ stepId, rows, runId, finalized }: WizardReviewStepProps) {
  const isAmbiguu = stepId === 'ambiguu';
  const [selected, setSelected] = useState<Set<number>>(() => defaultSelectedIds(rows));
  const [confirming, setConfirming] = useState(false);
  /** Per-row candidate ticks, keyed by row id. A row absent from this map has
   *  never been touched and means "every candidate ticked" (`candidateTicks`). */
  const [candidateSel, setCandidateSel] = useState<Record<number, number[]>>({});
  /** „12/40" progress while the ambiguu bulk loop runs; null when idle. */
  const [bulkProgress, setBulkProgress] = useState<number | null>(null);

  const bulkResolve = useBulkResolve(runId);
  const splitEntry = useSplitEntry(runId);
  const resolveEntry = useResolveEntry();

  useEffect(() => {
    setSelected(defaultSelectedIds(rows));
    setConfirming(false);
  }, [stepId, rows.length]);

  // A `revizuire`/`low_confidence` step can carry hundreds of rows, and each
  // row's `CorrectionPanel` is a heavy interactive subtree (its own debounced
  // nomenclator-search query + mutation hooks + keyboard handlers + candidate/
  // edit DOM). Mounting all of them at once froze the browser (a real report:
  // ~673 panels). Virtualize the list — same TanStack pattern as ResultsTable —
  // so only the on-screen panels (plus overscan) ever mount. `measureElement`
  // corrects the per-row estimate in a real browser; `ambiguu`'s read-only
  // candidate cards are much shorter than a full CorrectionPanel, hence the
  // stepId-split estimate.
  const parentRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (isAmbiguu ? 260 : 420),
    getItemKey: (index) => rows[index].id,
    overscan: 6,
  });

  if (rows.length === 0) {
    return <p className="py-10 text-center text-[13px] text-ink-3">Pas finalizat.</p>;
  }

  const ids = [...selected];

  /** Ticked candidate indexes for a row — every candidate until the operator says otherwise. */
  function candidateTicks(row: MapareRow): Set<number> {
    const stored = candidateSel[row.id];
    return new Set(stored ?? candidatesOf(row).map((_, i) => i));
  }
  function toggleCandidate(row: MapareRow, index: number) {
    setCandidateSel((m) => {
      const next = new Set(m[row.id] ?? candidatesOf(row).map((_, i) => i));
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return { ...m, [row.id]: [...next].sort((a, b) => a - b) };
    });
  }

  function toggle(id: number, checked: boolean) {
    setSelected((s) => {
      const n = new Set(s);
      if (checked) n.add(id);
      else n.delete(id);
      return n;
    });
    setConfirming(false);
  }

  /**
   * `ambiguu`'s bulk primary action: walk the selected rows and apply each
   * row's OWN `ambiguousAction` — most split, a row narrowed to one candidate
   * resolves instead. Sequential and per-row resilient: one row's 400/409
   * never aborts the rest (same contract as `RetryBar`'s batch loop), and the
   * per-row toast is suppressed so a 200-row batch doesn't raise 200 toasts.
   */
  async function commitAmbiguousBulk() {
    const targets = rows.filter((r) => selected.has(r.id));
    setBulkProgress(0);
    for (let i = 0; i < targets.length; i++) {
      const row = targets[i];
      const action = ambiguousAction(row, candidateTicks(row), finalized);
      try {
        if (action.kind === 'split') {
          await splitEntry.mutateAsync({ id: row.id, candidateIndexes: action.indexes, silent: true });
        } else if (action.kind === 'resolve') {
          await resolveEntry.mutateAsync({ id: row.id, runId, patch: resolvePatch(action), silent: true });
        }
      } catch {
        // Deliberately swallowed — the batch continues, and the row simply
        // stays in the step for the operator to handle on its own card.
      }
      setBulkProgress(i + 1);
    }
    setBulkProgress(null);
  }

  function commit() {
    if (isAmbiguu) void commitAmbiguousBulk();
    else bulkResolve.mutate({ ids, resolution: 'accepted' });
    setConfirming(false);
  }
  function handlePrimary() {
    if (!confirming && needsBulkConfirm(stepId, ids.length)) {
      setConfirming(true);
      return;
    }
    commit();
  }

  const bulkBusy = bulkProgress !== null || bulkResolve.isPending;
  const bulkLabel = isAmbiguu
    ? `Generează separat pentru ${formatNumberRo(ids.length)} ${ids.length === 1 ? 'rând' : 'rânduri'}`
    : primaryActionLabel(stepId, ids.length);
  const confirmSentence = isAmbiguu
    ? `Generezi rânduri separate pentru ${formatNumberRo(ids.length)} rânduri?`
    : `Validezi ${formatNumberRo(ids.length)} de rânduri?`;

  return (
    <div>
      <p className="mb-3 tabular-nums text-[12px] text-ink-3">{rows.length} rămase</p>
      <div ref={parentRef} className="max-h-[70vh] overflow-y-auto">
        <div style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            return (
              <div
                key={row.id}
                data-index={virtualRow.index}
                ref={rowVirtualizer.measureElement}
                // `transform` opens a stacking context per row, so a panel's
                // absolutely-positioned nomenclator dropdown would paint UNDER
                // the next row's wrapper. `focus-within:z-10` lifts whichever
                // row currently holds focus (i.e. whose search box is open —
                // the only time a dropdown shows) above its neighbours.
                className="focus-within:z-10"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <section className="border-b border-rule-soft py-5">
                  {isAmbiguu ? (
                    <>
                      <div className="mb-3 flex items-start gap-3">
                        <input
                          type="checkbox"
                          checked={selected.has(row.id)}
                          onChange={(e) => toggle(row.id, e.target.checked)}
                          disabled={finalized}
                          className="mt-1 size-4 shrink-0 accent-teal disabled:opacity-40"
                          aria-label={`Selectează ${row.code || row.name}`}
                        />
                        <div className="min-w-0">
                          <p className="font-mono text-[12px] text-ink-3">{row.code || '—'}</p>
                          <h3 className="text-[15px] font-semibold text-ink">{row.name}</h3>
                          {row.value && (
                            <p className="mt-0.5 text-[13px] text-ink-3">
                              <span className="font-medium text-ink-2">Valoare:</span> {row.value}
                            </p>
                          )}
                        </div>
                      </div>
                      <AmbiguousCandidates
                        row={row}
                        runId={runId}
                        finalized={finalized}
                        checked={candidateTicks(row)}
                        onToggleCandidate={(i) => toggleCandidate(row, i)}
                      />
                    </>
                  ) : (
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={(e) => toggle(row.id, e.target.checked)}
                        disabled={finalized}
                        className="mt-3 size-4 shrink-0 accent-teal disabled:opacity-40"
                        aria-label={`Selectează ${row.code || row.name}`}
                      />
                      <div className="min-w-0 flex-1">
                        <CorrectionPanel row={row} runId={runId} readOnly={finalized} layout="review" />
                      </div>
                    </div>
                  )}
                </section>
              </div>
            );
          })}
        </div>
      </div>

      {!finalized && (
        // Sticky bulk bar — same „pinned floating chrome" idiom as WizardListStep,
        // so the step's primary action stays reachable from row 1 on a long step.
        <div
          className={`sticky bottom-0 z-20 mt-2 border-t bg-bg pb-4 pt-3 shadow-[0_-6px_16px_-10px_rgba(0,0,0,0.35)] ${
            confirming ? 'border-warn' : 'border-rule'
          }`}
        >
          {confirming ? (
            <div className="flex flex-wrap items-center justify-end gap-3">
              <span className="text-[13px] text-ink-2">{confirmSentence}</span>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="text-[13px] font-medium text-ink-3 hover:text-ink-2"
              >
                Anulează
              </button>
              <Button type="button" onClick={commit} disabled={bulkBusy}>
                {isAmbiguu ? 'Da, generează' : 'Da, validează'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-end gap-3">
              <span className="tabular-nums text-[12px] text-ink-3">
                {bulkProgress !== null
                  ? `${formatNumberRo(bulkProgress)}/${formatNumberRo(ids.length)} procesate`
                  : `${formatNumberRo(ids.length)}/${formatNumberRo(rows.length)} selectate`}
              </span>
              <Button type="button" onClick={handlePrimary} disabled={ids.length === 0 || bulkBusy}>
                {bulkLabel}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Client-side split guard, mirroring the server's own two rejections
 * (`409 finalized`, `400 too_few`) so a split is never fired when the
 * request is certain to fail. Exported (not just inlined) so it's directly
 * unit-testable without a DOM, same pattern as `WizardListStep.tsx`'s
 * `runPrimaryAction`/`primaryActionResolution`.
 */
export function canSplitRow(row: MapareRow, finalized: boolean): boolean {
  if (finalized) return false;
  return candidatesOf(row).length >= 2;
}

function CandidateCard({
  c,
  label,
  checked,
  onToggle,
  disabled,
}: {
  c: MedocCandidate;
  label?: string;
  checked: boolean;
  onToggle: () => void;
  disabled: boolean;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-2.5 border px-3 py-2 ${
        checked ? 'border-rule bg-wash' : 'border-rule-soft bg-transparent opacity-60'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        disabled={disabled}
        className="size-4 shrink-0 accent-teal disabled:opacity-40"
        aria-label={`Include candidatul ${c.codMedoc || c.denumireMedoc}`}
      />
      <div className="min-w-0 flex-1">
        {label && <p className="text-[11px] uppercase tracking-wide text-ink-3">{label}</p>}
        <div className="flex items-center gap-2">
          <span className="shrink-0 font-mono text-[12px] text-ink-3">{c.codMedoc}</span>
          <span className="min-w-0 flex-1 truncate text-[13px] text-ink-2" title={c.denumireMedoc}>
            {c.denumireMedoc}
          </span>
          {typeof c.confidence === 'number' && (
            <span className="shrink-0 font-mono text-[12px] tabular-nums text-ink-3">
              {Math.round(c.confidence * 100)}%
            </span>
          )}
        </div>
      </div>
    </label>
  );
}

/**
 * The `ambiguu` step's per-row content — port of v1
 * `components/wizard/WizardSingleStep.tsx`'s `AmbiguousStep` (Rule Zero),
 * retokenized to v2's `ink`/`teal`/`rule-soft` tokens, no modal.
 *
 * 2026-08-21: the candidate cards are no longer read-only. Each carries a
 * checkbox (ticked by default), and the primary control follows
 * `ambiguousAction` — „Generează separat" for two or more ticks, „Confirmă
 * varianta" when the operator has narrowed it to exactly one. The selection
 * itself is owned by the parent step (virtualization would drop it here).
 */
function AmbiguousCandidates({
  row,
  runId,
  finalized,
  checked,
  onToggleCandidate,
}: {
  row: MapareRow;
  runId: number;
  finalized: boolean;
  checked: Set<number>;
  onToggleCandidate: (index: number) => void;
}) {
  const splitEntry = useSplitEntry(runId);
  const resolveEntry = useResolveEntry();
  const candidates = candidatesOf(row);
  const isPending = splitEntry.isPending || resolveEntry.isPending;
  const error = splitEntry.error ?? resolveEntry.error;
  const action = ambiguousAction(row, checked, finalized);

  function runAction() {
    if (action.kind === 'split') {
      splitEntry.mutate({ id: row.id, candidateIndexes: action.indexes });
    } else if (action.kind === 'resolve') {
      resolveEntry.mutate({ id: row.id, runId, patch: resolvePatch(action) });
    }
  }

  return (
    <>
      {candidates.length > 0 && (
        <div className="mb-3 space-y-2">
          {candidates.map((c, i) => (
            <CandidateCard
              key={i}
              c={c}
              label={row.primaryCandidate != null && i === 0 ? 'Principal' : `Alt. ${row.primaryCandidate != null ? i : i + 1}`}
              checked={checked.has(i)}
              onToggle={() => onToggleCandidate(i)}
              disabled={finalized || isPending}
            />
          ))}
        </div>
      )}
      {checked.size === 0 && !finalized && (
        <p className="mb-3 text-[12.5px] text-ink-3">
          Niciun candidat bifat — bifează cel puțin unul, sau marchează rândul nerezolvat.
        </p>
      )}
      {error && <p className="mb-3 text-[13px] text-bad">{error.message}</p>}
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          disabled={isPending || action.kind === 'none' || (action.kind === 'split' && !canSplitRow(row, finalized))}
          onClick={runAction}
        >
          {action.kind === 'resolve' ? 'Confirmă varianta' : 'Generează separat'}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isPending || finalized}
          onClick={() =>
            resolveEntry.mutate({ id: row.id, runId, patch: { reviewResolution: 'unresolved' } })
          }
        >
          Marchează nerezolvat
        </Button>
      </div>
    </>
  );
}
